import { createCampaignDetailApi, createImportBatchApi } from '@discovr/features';
import type { DeliverableInstance } from '@discovr/types';
import type { CreatorsRow } from './types';

// Re-export canonical label formatter and registry from types package.
export { fmtDeliverableType, DELIVERABLE_REGISTRY } from '@discovr/types';

// Singleton factory for the inline pill-count queries in the main component.
export const unifiedApi = createCampaignDetailApi();

// Singleton factory for the staged import-review wizard's 8 endpoints.
export const importBatchApi = createImportBatchApi();

export function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Profile-link URL for a platform username. */
export function profileLinkFor(platform: string | undefined, username: string): string {
  if (!username) return '';
  const p = (platform ?? '').toLowerCase();
  if (p === 'instagram') return `https://www.instagram.com/${username}`;
  return `https://www.youtube.com/${username}`;
}

/** Reinstating an admin-level removal or the brand's own rejection needs
 *  admin level on the campaign (BE: ReinstateNeedsAdminLevel); admin/creator
 *  rejections only need write level. */
export function reinstateNeedsAdminLevel(status: string): boolean {
  return status === 'removed_by_admin' || status === 'rejected_by_brand';
}

/** Returns true for terminal statuses (rejected or soft-removed) where no
 *  chat action is possible. Admin can reinstate any of them to pending. */
export function isRejectedStatus(status: string): boolean {
  return (
    status === 'rejected_by_brand' ||
    status === 'rejected_by_creator' ||
    status === 'rejected_by_admin' ||
    status === 'removed_by_admin'
  );
}

/**
 * resolvePreSelectedDeliverable — find the deliverable already picked for an entry,
 * via Zustand commercialDrafts or persisted is_selected flag.
 */
export function resolvePreSelectedDeliverable(
  entry: CreatorsRow,
  breakdown: null | undefined,
): string {
  const deliverables = entry.deliverables;
  if (deliverables.length === 0) return '';
  const TIER_MATCH: Record<string, string> = {
    integrated:  'integrated',
    dedicated:   'dedicated',
    shorts:      'short',
    reel:        'reel',
    story:       'story',
    stories:     'stories',
    post:        'post',
    static:      'static_post',
    carousel:    'carousel',
    non_collab:  'non_collab',
    link:        'link',
  };
  if (breakdown) {
    for (const [tier, substr] of Object.entries(TIER_MATCH)) {
      if (Number((breakdown as Record<string, unknown>)[tier] ?? 0) <= 0) continue;
      const match = deliverables.find((d) => d.type.toLowerCase().includes(substr));
      if (match) return match.type;
    }
  }
  const persisted = deliverables.find((d) => d.is_selected);
  return persisted ? persisted.type : '';
}

/** Sum of selected deliverable base prices on a row. */
export function sumSelectedDeliverableCost(deliverables: DeliverableInstance[]): number {
  return deliverables
    .filter((d) => d.is_selected)
    .reduce((sum, d) => sum + (d.base_price ?? 0), 0);
}
